import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bell } from 'lucide-react'
import { Popover, PopoverTrigger, PopoverContent } from '@claritio/frontend/components/ui/popover'
import type { NotificationsApi } from '@claritio/frontend/lib/useNotifications'
import type { AppNotification } from '@claritio/frontend/types'

// Presentational bell (ENG-129): polling/state live in useNotifications(),
// called once in AppShell — this renders safely in both sidebar instances.
export default function NotificationBell({ notifications }: { notifications: NotificationsApi }) {
  const { t } = useTranslation('admin')
  const { unread, items, loadList, openItem, markAll } = notifications
  const [open, setOpen] = useState(false)

  function onOpenChange(next: boolean) {
    setOpen(next)
    if (next) loadList()
  }

  async function handleOpen(n: AppNotification) {
    setOpen(false)
    await openItem(n)
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button className="relative rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label={t('notifications.aria')}>
          <Bell className="size-4" />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex size-4 items-center justify-center rounded-full bg-primary text-caption font-medium text-primary-foreground">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent>
        <div className="flex items-center justify-between px-2 py-1.5">
          <span className="text-xs font-medium text-muted-foreground">{t('notifications.title')}</span>
          {items.some((n) => !n.read_at) && (
            <button className="text-xs text-muted-foreground hover:text-foreground" onClick={markAll}>{t('notifications.markAllRead')}</button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-muted-foreground">{t('notifications.empty')}</p>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => handleOpen(n)}
                className={`flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-2 text-left hover:bg-muted ${n.read_at ? '' : 'bg-muted/40'}`}
              >
                <span className={`text-sm ${n.status === 'error' ? 'text-destructive' : 'text-foreground'}`}>{n.title}</span>
                {n.body && <span className="text-xs text-muted-foreground">{n.body}</span>}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
